/*
 #*===================================================================
 #*
 #* Licensed Materials - Property of Zuum Transportation Inc.
 #*
 #* Copyright Zuum 2015-2021. All Rights Reserved.
 #*
 #*===================================================================
 */

import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { ApiService } from 'app/services/api.service';
import {
  CLIENT_PLAN_TYPES,
  GlobalPriceConfiguration,
  Invoice,
  InvoiceItem,
  InvoiceItemCustomization,
} from '@core/models';
import { BillingTypeEnum, IApiResponse, InvoicesOverview } from '@truckstop-tms/zuum-lib';

@Injectable({
  providedIn: 'root',
})
export class InvoiceService {
  constructor(private http: HttpClient, private apiService: ApiService) {}

  createInvoice(clientId: number, payload: InvoiceItemCustomization[]): Observable<IApiResponse<Invoice>> {
    return this.http
      .post<IApiResponse<Invoice>>(`${this.apiService.superAdminApi}/invoice/${clientId}`, payload)
      .pipe(catchError((error: HttpErrorResponse) => throwError(error)));
  }

  update(invoice: Partial<Invoice>, itemsToRemove: InvoiceItem[], itemsToAdd: InvoiceItem[]): Observable<IApiResponse> {
    return this.http
      .put<IApiResponse>(`${this.apiService.superAdminApi}/invoice/${invoice.id}`, {
        invoice,
        itemsToAdd,
        itemsToRemove,
      })
      .pipe(catchError((error: HttpErrorResponse) => throwError(error)));
  }

  delete(invoiceId: number): Observable<IApiResponse> {
    return this.http
      .delete<IApiResponse>(`${this.apiService.superAdminApi}/invoice/${invoiceId}`)
      .pipe(catchError((error: HttpErrorResponse) => throwError(error)));
  }

  generatePdf(invoiceId: number): Observable<IApiResponse<{ url: string }>> {
    return this.http
      .post<IApiResponse>(`${this.apiService.superAdminApi}/invoice/pdf/${invoiceId}`, {})
      .pipe(catchError((error: HttpErrorResponse) => throwError(error)));
  }

  sendInvoice(invoiceId: number): Observable<IApiResponse<Invoice>> {
    return this.http
      .post<IApiResponse<Invoice>>(`${this.apiService.superAdminApi}/invoice/send/${invoiceId}`, {})
      .pipe(catchError((error: HttpErrorResponse) => throwError(error)));
  }

  applyDiscount(invoiceId: number, code: string): Observable<IApiResponse<Invoice>> {
    return this.http
      .put<IApiResponse<Invoice>>(`${this.apiService.superAdminApi}/invoice/apply-discount/${invoiceId}`, { code })
      .pipe(catchError((error: HttpErrorResponse) => throwError(error)));
  }

  simulateInvoiceGeneration(clientId: number): Observable<IApiResponse<boolean>> {
    return this.http
      .post<IApiResponse<boolean>>(`${this.apiService.superAdminApi}/invoice/saas/simulate/invoice-generation/`, {
        clientId,
      })
      .pipe(catchError((error: HttpErrorResponse) => throwError(error)));
  }

  reSendInvoice(invoiceId: number): Observable<IApiResponse<Invoice>> {
    return this.http
      .post<IApiResponse>(`${this.apiService.superAdminApi}/invoice/re-send/${invoiceId}`, {})
      .pipe(catchError((error: HttpErrorResponse) => throwError(error)));
  }

  getInvoicesOverview(clientId: number): Observable<IApiResponse<InvoicesOverview>> {
    return this.http
      .get<IApiResponse>(`${this.apiService.superAdminApi}/invoice/${clientId}/overview`)
      .pipe(catchError((error: HttpErrorResponse) => throwError(error)));
  }

  getInvoicesByClientId(clientId: number): Observable<IApiResponse<Invoice[]>> {
    return this.http
      .get<IApiResponse>(`${this.apiService.superAdminApi}/invoice/by-client-id/${clientId}`)
      .pipe(catchError((error: HttpErrorResponse) => throwError(error)));
  }

  getInvoiceById(invoiceId: number): Observable<IApiResponse<Invoice>> {
    return this.http
      .get<IApiResponse>(`${this.apiService.superAdminApi}/invoice/${invoiceId}`)
      .pipe(catchError((error: HttpErrorResponse) => throwError(error)));
  }

  getDefaultInvoiceItemsFromSaasInvoice(
    clientPriceConfig: GlobalPriceConfiguration[],
    clientActiveAddOns: any,
    numberOfUsers: number,
    plan: { plan: { type: CLIENT_PLAN_TYPES }; billingType: BillingTypeEnum }
  ): Observable<InvoiceItem[]> {
    return this.http
      .post<InvoiceItem[]>(`${this.apiService.superAdminApi}/invoice/saas/invoice/invoice-items/default`, {
        clientPriceConfig,
        clientActiveAddOns: clientActiveAddOns || [],
        numberOfUsers,
        plan,
      })
      .pipe(catchError((error: HttpErrorResponse) => throwError(error)));
  }

  getBlobFromInvoicePdfUrl(url: string): Observable<any> {
    let headers = new HttpHeaders();
    headers = headers.set('Accept', 'application/pdf');
    return this.http
      .get<IApiResponse>(url, { headers, responseType: 'blob' as 'json' })
      .pipe(catchError((error: HttpErrorResponse) => throwError(error)));
  }
}
